import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.object.from-entries.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.string.includes.js";
import "core-js/modules/es.string.trim.js";
/**
 * Manage Axeptio consent for vendors.
 * Use this composable to get or react to user consent for specific third-party services.
 *
 * @returns Consent utilities
 */
export function useAxeptio() {
  /**
   * Check if the user has given consent for a specific vendor.
   * @param vendor - Vendor name to check (e.g., 'google', 'facebook')
   * @returns True if the vendor is authorized, false otherwise
   */
  function getConsent(vendor) {
    var _vendorConsentCookie$, _vendorConsentCookie$2;
    var vendorConsentCookie = useCookie('axeptio_authorized_vendors', {
      readonly: true
    });
    var authorizedVendors = (_vendorConsentCookie$ = (_vendorConsentCookie$2 = vendorConsentCookie.value) === null || _vendorConsentCookie$2 === void 0 ? void 0 : _vendorConsentCookie$2.split(',')) !== null && _vendorConsentCookie$ !== void 0 ? _vendorConsentCookie$ : [];
    return authorizedVendors.includes(vendor);
  }

  /**
   * Executes a callback only if the user has given consent for the specified vendor.
   * If consent is not yet collected, it waits for Axeptio to initialize and then runs the callback if authorized.
   *
   * @param vendor - Vendor name to check
   * @param callback - Function to run if the vendor is authorized
   */
  function onConsent(vendor, callback) {
    var _window, _window$axeptioSDK;
    if (getConsent(vendor)) {
      callback();
      return;
    }
    if (typeof ((_window = window) === null || _window === void 0 ? void 0 : (_window$axeptioSDK = _window.axeptioSDK) === null || _window$axeptioSDK === void 0 ? void 0 : _window$axeptioSDK.on) === 'function') {
      window.axeptioSDK.on('cookies:complete', function () {
        if (getConsent(vendor)) {
          callback();
        }
      });
    } else {
      window.addEventListener('axeptio:init', function () {
        var _window2, _window2$axeptioSDK;
        if (typeof ((_window2 = window) === null || _window2 === void 0 ? void 0 : (_window2$axeptioSDK = _window2.axeptioSDK) === null || _window2$axeptioSDK === void 0 ? void 0 : _window2$axeptioSDK.on) === 'function') {
          window.axeptioSDK.on('cookies:complete', function () {
            if (getConsent(vendor)) {
              callback();
            }
          });
        }
      }, {
        once: true
      });
    }
  }

  /**
   * Listen for Axeptio consent updates for a specific vendor, and return its consent status.
   *
   * @param vendor - Vendor name to check (e.g. 'dynamicyield', 'google_analytics')
   * @param callback - Function to run with the vendor consent status (true = authorized, false = denied)
   */
  function onConsentChange(vendor, callback) {
    /**
     * @function handleConsent
     * @description Check vendor consent and run callback with result
     * @returns {void}
     */

    function handleConsent() {
      callback(getConsent(vendor));
    }
    if (typeof window !== 'undefined') {
      var _window$axeptioSDK2;
      if (typeof ((_window$axeptioSDK2 = window.axeptioSDK) === null || _window$axeptioSDK2 === void 0 ? void 0 : _window$axeptioSDK2.on) === 'function') {
        window.axeptioSDK.on('cookies:complete', handleConsent);
      } else {
        window._axcb = window._axcb || [];
        window._axcb.push(function (sdk) {
          sdk.on('cookies:complete', handleConsent);
        });
      }
    }
  }

  /**
   * Returns full consent context from Axeptio (to inject into Segment)
   */
  function getConsentContext() {
    var _vendorConsentCookie$3, _vendorConsentCookie$4;
    var vendorConsentCookie = useCookie('axeptio_authorized_vendors', {
      readonly: true
    });
    var authorizedVendors = (_vendorConsentCookie$3 = (_vendorConsentCookie$4 = vendorConsentCookie.value) === null || _vendorConsentCookie$4 === void 0 ? void 0 : _vendorConsentCookie$4.split(',')) !== null && _vendorConsentCookie$3 !== void 0 ? _vendorConsentCookie$3 : [];
    var categoryPreferences = Object.fromEntries(authorizedVendors.map(function (vendor) {
      return vendor.trim();
    }).filter(function (vendor) {
      return vendor.length > 0;
    }).map(function (vendor) {
      return [vendor.toLowerCase(), true];
    }));
    return {
      consent: {
        categoryPreferences: categoryPreferences
      }
    };
  }
  return {
    getConsent: getConsent,
    onConsent: onConsent,
    onConsentChange: onConsentChange,
    getConsentContext: getConsentContext
  };
}